import { Injectable } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute, Params, QueryParamsHandling, Router } from '@angular/router';
import { Observable } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';

import { isEqual, orderBy } from 'lodash';
import { IPaginatedData, IPaginationParams } from '../interfaces/pagination.interface';
import { $paginationParams } from '../constants/pagination-params';
import { SharedCommonUtility } from '../../../shared/utils/common.utility';
import { CommonUtility } from '../utility/common.utility';
import { $sortingOrder } from '../../../shared/constants/sort';

@Injectable({
  providedIn: 'root',
})
export class PaginationService {
  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
  ) {}

  public updateUrlQueryParams<T extends IPaginationParams>(params: T, paramsHandling: QueryParamsHandling = 'merge'): void {
    this.router
      .navigate([], {
        queryParams: params,
        queryParamsHandling: paramsHandling,
        replaceUrl: true,
      })
      .then();
  }

  public updateFormValues<T extends IPaginationParams>(params: T, form: UntypedFormGroup, formFieldsToUpdate: string[]): void {
    formFieldsToUpdate
      .filter((field: string) => form.contains(field))
      .forEach((field: string) =>
        form.get(field).setValue(SharedCommonUtility.isNullishOrEmpty(params[field]) ? '' : params[field], {
          onlySelf: true,
          emitEvent: false,
        }),
      );
  }

  public getFormValueChanges$(form: UntypedFormGroup, formFields: string[]): Observable<Params> {
    return form.valueChanges.pipe(
      map((values: Record<string, any>) => {
        const params: Record<string, any> = {};

        formFields.forEach((field: string) => {
          params[field] = values[field];
        });

        return params;
      }),
      distinctUntilChanged(isEqual),
    );
  }

  public getPaginationParamsFromQueryParams<T extends IPaginationParams>(
    defaultPaginationParams: T,
    sortableColumns: string[],
    fields: string[],
  ): T {
    const queryParams: Params = this.activatedRoute.snapshot.queryParams;
    const paginationParams: T = { ...defaultPaginationParams };

    if (SharedCommonUtility.isPositiveInteger(queryParams[$paginationParams.pageNumber])) {
      paginationParams[$paginationParams.pageNumber] = Number(queryParams[$paginationParams.pageNumber]);
    }

    if (SharedCommonUtility.isPositiveInteger(queryParams[$paginationParams.pageSize])) {
      paginationParams[$paginationParams.pageSize] = Number(queryParams[$paginationParams.pageSize]);
    }

    if (CommonUtility.isValidSortingQueryParams(queryParams, sortableColumns)) {
      paginationParams[$paginationParams.sortBy] = queryParams[$paginationParams.sortBy];
      paginationParams[$paginationParams.direction] = queryParams[$paginationParams.direction];
    }

    this.setPaginationParams(paginationParams, queryParams, fields);

    return paginationParams as T;
  }

  private setPaginationParams<T extends IPaginationParams>(paginationParams: T, queryParams: Params, fields: string[]): void {
    fields
      .filter((field: string) => SharedCommonUtility.notNullishOrEmpty(queryParams[field]))
      .forEach((field: string) => {
        paginationParams[field] = queryParams[field];
      });
  }

  /**
   * paginate a list of data locally
   *
   * @param items a list of data
   * @param params a pagination params.
   * @param filter to filter the items before paginating items
   */
  public paginate<T, P extends IPaginationParams>(
    items: T[],
    params: P,
    filter: (item: T, params: P) => boolean = (): boolean => true,
  ): IPaginatedData<T> {
    let result: T[] = items.filter((item: T) => filter(item, params));
    const total: number = result.length;

    const direction: $sortingOrder = params[$paginationParams.direction];
    if (SharedCommonUtility.notNullishOrEmpty(direction) && direction !== $sortingOrder.all) {
      const sortBy: string = params[$paginationParams.sortBy];

      result = orderBy(result, [sortBy], [direction]);
    }

    if (CommonUtility.isValidPaginationQueryParams(params)) {
      const pageNumber: number = params[$paginationParams.pageNumber];
      const pageSize: number = params[$paginationParams.pageSize];

      result = result.slice((pageNumber - 1) * pageSize, pageNumber * pageSize);
    }

    return {
      items: result,
      _total: total,
    };
  }
}
